import { isEqual } from 'underscore';
import { createModel } from '@rematch/core';

import type { ABTestStatus } from './ABTest.service';
import {
  assignABTest,
  checkTestStatus,
  forceABTestAllocation,
} from './ABTest.service';
import type { RootModel } from '../models';
import type {
  ABTestVariationDetails,
  Assignment,
  FlagList,
} from './ABTest.types';

import FeatureFlags from '../../modules/FeatureFlags';
import Deferred from '../../testUtilities/deferred';

type AssignTestNumbers = {
  percent: number,
  variations?: null,
} | {
  percent?: null,
  variations: ABTestVariationDetails,
};

export type AssignTestRequest = {
  name: string,
} & AssignTestNumbers;

type Assignments = {
  [key: string]: Assignment;
};

type State = {
  isLoaded: boolean;
  oldFlags: FlagList;
  assignments: Assignments;
  hasLoadedPromise: Promise<State>;
};

const hasLoadedDeferred = new Deferred<State>();

const model = createModel<RootModel>()({
  name: 'abTest',

  state: {
    assignments: {},
    hasLoadedPromise: hasLoadedDeferred.promise,
  } as State,

  reducers: {
    onUpdateComplete(state: State, {
      oldFlags,
      assignments,
    }: {
      oldFlags: FlagList,
      assignments: Assignments,
    }) {
      const updatedState: State = {
        ...state,
        oldFlags,
        assignments,
        isLoaded: true,
      };
      hasLoadedDeferred.resolve(updatedState);
      return updatedState;
    },
    onTestsAssigned(state: State, testAssignments: {
      name: string,
      inTest: boolean,
      isEnabled: boolean,
      variationName?: string,
      allocationPercent?: number,
    }[]) {
      return {
        ...state,
        assignments: testAssignments.reduce((previousValue, {
          name,
          inTest,
          isEnabled,
          variationName,
          allocationPercent,
        }) => ({
          ...previousValue,
          [name]: {
            inTest,
            isEnabled,
            variationName,
            allocationPercent,
            isAssigned: isEnabled,
          },
        }), state.assignments),
      };
    },

    onTestAssigned(state: State, {
      name,
      inTest,
      isEnabled,
      variationName,
      allocationPercent,
    }: {
      name: string,
      inTest: boolean,
      isEnabled: boolean,
      variationName?: string,
      allocationPercent?: number,
    }) {
      return {
        ...state,
        assignments: {
          ...state.assignments,
          [name]: {
            inTest,
            isEnabled,
            variationName,
            allocationPercent,
            isAssigned: isEnabled,
          },
        },
      };
    },
  },

  effects: dispatch => ({
    updateFlags: async (flagList: FlagList, rootState) => {
      const assignments: Assignments = {};
      const { oldFlags } = rootState.abTest;

      if (!isEqual(flagList, oldFlags)) {
        Object.keys(flagList)
          .forEach(key => {
            const isEnabled = flagList[key];
            if (isEnabled) {
              const status: ABTestStatus = checkTestStatus(key);

              assignments[key] = {
                isAssigned: status.assigned,
                inTest: status.choice,
                isEnabled: true,
              };

              if (status.variant) {
                assignments[key].variationName = status.variant;
              }

              if (status.allocation) {
                assignments[key].allocationPercent = status.allocation;
              }
            } else {
              assignments[key] = {
                isEnabled,
                inTest: false,
                isAssigned: false,
              };
            }
          });

        dispatch.abTest.onUpdateComplete({
          oldFlags: flagList,
          assignments,
        });
      }
    },

    forceAssignTest: async ({
      name,
      allocation,
    }: {
      name: string,
      allocation: number | boolean,
    }): Promise<Assignment> => {
      const isEnabled = await FeatureFlags.isEnabled(name);

      if (isEnabled) {
        const status = forceABTestAllocation(
          name,
          allocation,
        );

        dispatch.abTest.onTestAssigned({
          name,
          isEnabled,
          inTest: status.choice,
          variationName: status.variant,
          allocationPercent: status.allocation,
        });

        return {
          isEnabled,
          inTest: status.choice,
          isAssigned: status.assigned,
          variationName: status.variant,
          allocationPercent: status.allocation,
        };
      }

      dispatch.abTest.onTestAssigned({
        name,
        isEnabled,
        inTest: false,
      });

      return {
        inTest: false,
        isEnabled: false,
        isAssigned: false,
      };
    },
    assignTests: async (
      assignTestRequests: AssignTestRequest[],
    ): Promise<void> => {
      const isTestEnabledList = await Promise.all(
        assignTestRequests.map(assignTestRequest => FeatureFlags.isEnabled(assignTestRequest.name)),
      );
      const assignments = isTestEnabledList.map((isEnabled, index) => {
        const {
          name,
          variations,
          percent,
        } = assignTestRequests[index];
        if (isEnabled) {
          const status = assignABTest({
            testName: name,
            variations: variations || {
              control: 1 - percent,
              test: percent,
            },
          });

          return {
            name,
            isEnabled,
            inTest: status.choice,
            variationName: status.variant,
            allocationPercent: status.allocation,
          };
        }
        return {
          name,
          isEnabled,
          inTest: false,
        };
      });

      dispatch.abTest.onTestsAssigned(assignments);
    },
    assignTest: async ({
      name,
      percent,
      variations,
    }: AssignTestRequest): Promise<Assignment> => {
      const isEnabled = await FeatureFlags.isEnabled(name);

      if (isEnabled) {
        const status = assignABTest({
          testName: name,
          variations: variations || {
            control: 1 - percent,
            test: percent,
          },
        });

        dispatch.abTest.onTestAssigned({
          name,
          isEnabled,
          inTest: status.choice,
          variationName: status.variant,
          allocationPercent: status.allocation,
        });

        return {
          isEnabled,
          inTest: status.choice,
          isAssigned: status.assigned,
          variationName: status.variant,
          allocationPercent: status.allocation,
        };
      }

      dispatch.abTest.onTestAssigned({
        name,
        isEnabled,
        inTest: false,
      });

      return {
        inTest: false,
        isEnabled: false,
        isAssigned: false,
      };
    },
  }),
});

export default model;
